// price_estimate.js
//
// What a year in our system would cost a customer, and what the same year costs them where they
// are now, given the usage read out of their SAF-T file.
//
// The prices are not here. The page hands us _data/prices.yml and the vendors in
// _data/competitor_prices.yml as JSON, so this file holds only the arithmetic, and it follows
// the same rules as plugins/luca/price_comparison.rb:
//
//   * a missing field costs nothing
//   * `ikke_mulig` means the plan cannot do it, `inkludert` that it costs nothing extra, and
//     `kun_enk` that the year-end is sold to sole proprietors only
//   * a received EHF is a voucher too, priced at `motta_ehf` instead of `bilagspris`
//   * a cap on vouchers per year costs `pris_per_bilag_over_grensen` for each one past it
//
// Unlike the comparison tables, which price a fixed "typical example", every number here is per
// year and comes from the customer's own books.

import { namePattern } from './saft_reader'

const MONTHS = 12

const usable = (raw) => typeof raw === 'number'
const price = (raw) => (usable(raw) ? raw : raw === 'inkludert' || raw == null ? 0 : null)

// Our own price, from _data/prices.yml.
//
// Payroll is priced per employee, so `lonn` is paid once for each of them -- the same reading
// Luca::PriceComparison#own_plan makes.
export function ownPrice(prices, usage) {
  const lines = []

  if (usage.form === 'holding') {
    lines.push({
      key: 'grunnpris',
      label: 'Holding, inkludert bankintegrasjon og årsavslutning',
      amount: prices.holding,
    })
  } else {
    // Numo's yearly plan is a round sum a year, not a round sum a month, and 191,58 kr/mnd reads
    // worse than saying nothing about the month.
    const perMonth = Number.isInteger(prices.ar) ? `${kr(prices.ar)} kr/mnd ` : ''
    lines.push({ key: 'grunnpris', label: `Abonnement, ${perMonth}betalt årlig`, amount: prices.ar * MONTHS })
    if (usage.bank) {
      lines.push({ key: 'bank', label: 'Bankintegrasjon', amount: prices.bankstotte * MONTHS })
      if (usage.supplierPayments > 0) {
        lines.push({
          key: 'betaling',
          label: `${count(usage.supplierPayments)} betalinger fra regnskapet à ${prices.bank_betaling} kr`,
          amount: prices.bank_betaling * usage.supplierPayments,
        })
      }
    }
    lines.push({
      key: 'arsoppgjor',
      label: usage.form === 'enk' ? 'Årsavslutning for ENK' : 'Årsavslutning for AS',
      amount: usage.form === 'enk' ? prices.arsavslutning_enk : prices.arsavslutning_as,
    })
  }

  if (usage.employees > 0) {
    lines.push({
      key: 'lonn',
      label: `Lønn for ${count(usage.employees)} ${usage.employees === 1 ? 'ansatt' : 'ansatte'}`,
      amount: prices.lonn * usage.employees * MONTHS,
    })
  }

  return withTotal(lines)
}

// Every plan the vendor sells, priced for this usage, the likeliest one first: the cheapest of
// the plans that lack the fewest of the services the customer uses, preferring one whose voucher
// cap they stay under. Usually that is a plan that does everything, but not always -- Visma has no
// payroll in any plan, and Tripletex's 500-voucher cap is on all four.
export function competitorPrices(vendor, usage) {
  const plans = Object.entries(vendor.pakker).map(([key, plan]) => planPrice(vendor, key, plan, usage))
  return plans.sort(
    (a, b) =>
      a.impossible - b.impossible ||
      a.blockers.length - b.blockers.length ||
      a.overCap - b.overCap ||
      a.total - b.total,
  )
}

function planPrice(vendor, key, plan, usage) {
  const lines = []
  const notes = []
  const blockers = []
  let impossible = false
  let overCap = false

  const base = price(usage.form === 'holding' ? (plan.manedspris_holding ?? plan.manedspris) : plan.manedspris)
  if (base === null) impossible = true
  lines.push({ key: 'grunnpris', label: `Abonnement, ${kr(base)} kr/mnd`, amount: (base || 0) * MONTHS })

  // Every voucher in the file is billed once. A received EHF is billed at `motta_ehf`, a sent
  // one at `sende_ehf` and a bill paid from the books at `betaling_ut` -- each of them a voucher
  // too, so none of them pays `bilagspris` on top. The rest pay `bilagspris`. That is how the
  // comparison tables count as well: their 40 vouchers include the 20 received EHFs, and the 20
  // sent invoices come on top.
  const payment = price(plan.betaling_ut)
  const paysFromBooks = usage.bank && usage.form !== 'holding' && payment > 0
  const received = Math.min(usage.purchaseEhf, usage.vouchers)
  const sent = Math.min(usage.salesEhf, usage.vouchers - received)
  const paid = paysFromBooks ? Math.min(usage.supplierPayments, usage.vouchers - received - sent) : 0
  const others = usage.vouchers - received - sent - paid
  const voucherRate = price(plan.bilagspris) || 0
  const receivedRate = price(plan.motta_ehf) || 0
  const sentRate = price(plan.sende_ehf) || 0
  if (voucherRate > 0 && others > 0) {
    lines.push({
      key: 'bilag',
      label: `${count(others)} ${received + sent + paid > 0 ? 'andre ' : ''}bilag à ${kr(voucherRate)} kr`,
      amount: voucherRate * others,
    })
  }
  if (receivedRate > 0 && received > 0) {
    lines.push({
      key: 'motta_ehf',
      label: `${count(received)} mottatte EHF-fakturaer à ${kr(receivedRate)} kr`,
      amount: receivedRate * received,
    })
  }
  if (sentRate > 0 && sent > 0) {
    lines.push({
      key: 'sende_ehf',
      label: `${count(sent)} sendte EHF-fakturaer à ${kr(sentRate)} kr`,
      amount: sentRate * sent,
    })
  }

  const cap = plan.bilag_inkludert_per_ar
  if (usable(cap) && usage.vouchers > cap) {
    if (usable(plan.pris_per_bilag_over_grensen)) {
      const excess = usage.vouchers - cap
      lines.push({
        key: 'over_grensen',
        label: `${count(excess)} bilag over grensen på ${count(cap)} à ${kr(plan.pris_per_bilag_over_grensen)} kr`,
        amount: excess * plan.pris_per_bilag_over_grensen,
      })
    } else {
      overCap = true
      notes.push(
        `${plan.navn} har et tak på ${count(cap)} bilag i året, og du fører flere. Leverandøren ` +
          'oppgir ingen pris per bilag over taket, så den reelle prisen er trolig høyere enn anslaget.',
      )
    }
  }

  if (usage.bank) {
    const bank = price(plan.bankmodul)
    if (bank === null) {
      blockers.push('har ikke bankintegrasjon')
      lines.push({ key: 'bank', label: 'Bankintegrasjon tilbys ikke', amount: null })
    } else if (bank > 0) {
      lines.push({ key: 'bank', label: 'Bankintegrasjon', amount: bank * MONTHS })
    }
    // Holding is priced without bill payments on our side too, so they stay out of both.
    if (usage.form !== 'holding' && usage.supplierPayments > 0) {
      if (payment === null) {
        blockers.push('kan ikke betale regninger fra regnskapet')
        lines.push({ key: 'betaling', label: 'Betaling fra regnskapet tilbys ikke', amount: null })
      } else if (payment > 0) {
        lines.push({
          key: 'betaling',
          label: `${count(paid)} betalinger fra regnskapet à ${kr(payment)} kr`,
          amount: payment * paid,
        })
      }
    }
  }

  if (usage.employees > 0) {
    const payroll = payrollPrice(vendor, plan, usage.employees)
    if (payroll === null) {
      blockers.push('har ikke lønn')
      lines.push({ key: 'lonn', label: 'Lønn tilbys ikke', amount: null })
    } else {
      lines.push({
        key: 'lonn',
        label: `Lønn for ${count(usage.employees)} ${usage.employees === 1 ? 'ansatt' : 'ansatte'}`,
        amount: payroll * MONTHS,
      })
    }
  }

  const yearEnd = yearEndPrice(vendor, plan, usage.form)
  if (yearEnd === null) {
    lines.push({
      key: 'arsoppgjor',
      label: usage.form === 'enk' ? 'Årsoppgjør tilbys ikke' : 'Årsoppgjør for AS tilbys ikke',
      amount: null,
    })
  } else {
    lines.push({ key: 'arsoppgjor', label: 'Årsoppgjør', amount: yearEnd })
  }

  if (usable(plan.etablering) && plan.etablering > 0) {
    notes.push(`${plan.navn} tar i tillegg ${kr(plan.etablering)} kroner i etablering, som ikke er tatt med her.`)
  }
  for (const note of [vendor.notat, plan.notat, plan.andre_begrensninger]) {
    if (note) notes.push(note)
  }

  return { key, navn: plan.navn, blockers, impossible, overCap, notes, ...withTotal(lines) }
}

// A payroll price that scales with the number of employees. PowerOffice states it as a module
// price plus a fee per employee paid; everyone else is priced per employee, and the data holds
// that as the cost of two of them.
function payrollPrice(vendor, plan, employees) {
  const module = vendor.moduler?.lonn
  if (module && typeof module === 'object' && usable(module.tillegg_per_ansatt)) {
    return module.pris + module.tillegg_per_ansatt * employees
  }
  const two = price(plan.lonn_2_ansatte)
  if (two === null) return null
  return (two / 2) * employees
}

// `kun_enk` and `ikke_mulig` both come back as null: there is no year-end to buy for this company.
function yearEndPrice(vendor, plan, form) {
  const enk = vendor.moduler?.arsoppgjor_enk
  if (form === 'enk' && enk != null) return price(enk)
  return price(plan.arsoppgjor)
}

// The two totals put side by side. Whatever the competitor does not offer at all -- a line priced
// as null -- is left out of our total too, or the comparison would charge us for something they
// simply do not do. Holding is the exception that takes care of itself: bank and year-end are
// inside our one price there, with no line of their own to take out.
//
// `billed` is what the file shows the competitor actually invoiced in a year. When there is one,
// it is their side of the comparison, and the estimate from the price list only explains it.
export function comparison(own, theirs, billed = null) {
  const missing = theirs.lines.filter((line) => line.amount === null).map((line) => line.key)
  const excluded = own.lines.filter((line) => missing.includes(line.key))
  const ownTotal = own.total - excluded.reduce((sum, line) => sum + line.amount, 0)
  const theirTotal = billed ?? theirs.total
  return {
    own: ownTotal,
    theirs: theirTotal,
    saving: theirTotal - ownTotal,
    billed: billed !== null,
    missing,
    excluded: excluded.map((line) => line.key),
  }
}

// What each line is called in a sentence, for the notes on what the comparison left out.
export const SERVICE_NAMES = {
  arsoppgjor: 'årsoppgjør',
  bank: 'bankintegrasjon',
  betaling: 'betaling av regninger fra regnskapet',
  lonn: 'lønn',
}

function withTotal(lines) {
  const rounded = lines.map((line) => ({ ...line, amount: line.amount === null ? null : Math.round(line.amount) }))
  return { lines: rounded, total: rounded.reduce((sum, line) => sum + (line.amount || 0), 0) }
}

// Usage as read from the file, scaled to twelve months and rounded to whole things.
export function usageFromSummary(summary) {
  const factor = summary.months ? MONTHS / summary.months : 1
  const perYear = (n) => Math.round(n * factor)
  const payroll = summary.payrollTransactions > 0
  const isAs = summary.hasShareCapital || /\bASA?\b\.?\s*$/i.test(summary.companyName || '')
  const holding =
    isAs &&
    !payroll &&
    summary.salesInvoices === 0 &&
    (/holding/i.test(summary.companyName || '') || summary.hasInvestments)

  return {
    vouchers: perYear(summary.transactions),
    salesEhf: perYear(summary.salesInvoicesToBusinesses),
    purchaseEhf: perYear(summary.purchaseInvoicesFromBusinesses),
    supplierPayments: perYear(summary.supplierPayments),
    employees: payroll ? employeeEstimate(summary).count : 0,
    // A file cannot tell a bank integration from bank lines keyed in by hand -- nearly every
    // company posts to 1920 -- so bank integration is the customer's to tick. Holding is the
    // exception: it comes with bank integration, so a company we suggest it for has it on.
    bank: holding,
    form: holding ? 'holding' : isAs ? 'as' : 'enk',
  }
}

// What a payroll costs a month for each employee, on 5000-5099 -- salary, holiday pay and sick
// pay, before employer's tax. It sits below the full-time average because a small business pays
// part-timers and apprentices too, and each of them is a payslip to price.
export const PAYROLL_PER_EMPLOYEE = 35000

// How many employees the file pays. Fiken's salary sub-accounts and Uni Micro's employee analysis
// name each of them, and then we count them. Most files post a payroll run as one lump, so
// otherwise the count is the salary bill in a month that ran payroll, divided by what one
// employee typically costs.
export function employeeEstimate(summary) {
  if (summary.employees > 0) return { count: summary.employees, perMonth: null }
  if (!(summary.payrollCost > 0) || !(summary.payrollMonths > 0)) return { count: 1, perMonth: null }

  const perMonth = summary.payrollCost / summary.payrollMonths
  return { count: Math.max(1, Math.round(perMonth / PAYROLL_PER_EMPLOYEE)), perMonth: Math.round(perMonth) }
}

// What to look for in the books to find the vendor's own invoices: the first word of its name,
// as a word. "Visma eAccounting" bills as Visma Software AS, "Tripletex" as Tripletex AS. A
// vendor whose first word is too short to search for names itself in `saft_fakturanavn`.
export function billers(vendors) {
  return Object.entries(vendors).map(([key, vendor]) => ({
    key,
    pattern: namePattern(vendor.saft_fakturanavn || [vendor.navn.split(' ')[0]]),
  }))
}

// Whether any of the vendor's plans has a price we can count on. Uni Micro sells only through
// accounting firms and banks, so all it has is what the customer's own books say it charged.
export function hasPriceList(vendor) {
  return Object.values(vendor.pakker).some((plan) => plan.manedspris !== 'ikke_mulig')
}

// The vendor whose software wrote the file, matched on the header's SoftwareCompanyName and
// SoftwareID against the `saft_programvare` names in _data/competitor_prices.yml.
export function detectVendor(vendors, summary) {
  const written = `${summary.softwareCompanyName} ${summary.softwareId}`.toLowerCase()
  return (
    Object.entries(vendors).find(([, vendor]) =>
      vendor.saft_programvare.some((name) => written.includes(name.toLowerCase())),
    )?.[0] || null
  )
}

const numberFormat = new Intl.NumberFormat('nb-NO', { maximumFractionDigits: 2 })
export const kr = (n) => (n === null || n === undefined ? '–' : numberFormat.format(n))
const count = (n) => numberFormat.format(n)
